<template>
  <CCard class="settings-card">
    <CCardHeader>
      <div class="section-heading">
        <span class="section-icon"><CIcon name="cil-fingerprint"/></span>
        <div>
          <strong>パスキー</strong>
          <!--
            **パスキーはパスワードの入口を閉じない**（束22）。以前は「盗まれる
            パスワードがありません」と書いていたが、パスワードでのログインは
            そのまま使える（閉じられるのは会社単位の設定だけ）。
          -->
          <p>
            この端末の指紋・顔・PINでログインします。
            パスワードを打ち込む必要がなくなります。
          </p>
        </div>
      </div>
    </CCardHeader>

    <CCardBody>
      <div v-if="loading" class="security-settings-loading">
        <CSpinner color="primary"/>
        <span>登録済みのパスキーを読み込んでいます…</span>
      </div>

      <template v-else>
        <CAlert
          v-if="errorMessage"
          color="danger"
          close-button
          @update:show="errorMessage = ''"
        >{{ errorMessage }}</CAlert>

        <CAlert
          v-if="message"
          color="success"
          close-button
          @update:show="message = ''"
        >{{ message }}</CAlert>

        <!--
          登録はできるがログインには使えない、という状態がありうる。
          **黙って登録させない。** 登録したのに使えないと、利用者は
          自分の操作が失敗したと思う。
        -->
        <CAlert v-if="credentials.length && !offered" color="warning">
          パスキーは登録されていますが、<b>現在ログインに使用できません</b>。
          {{ productName }}でパスキーでのログインを現在提供していないためです。
          サポートへお問い合わせください。
        </CAlert>

        <!--
          **使えないときは、理由を必ず出す。**

          押せるものとして見せないだけでは、「登録されたパスキーは
          ありません」しか残らず、なぜ登録できないのか分からない。
          黙って操作を消すのは、壊れているのと見分けがつかない。

          **向き先が変わるので2つに分ける。**用意されていないのは
          こちら（PREIKA）側の話で、会社の管理者に言っても始まらない。
          隣の外部アカウントの欄と言い回しを揃えてある。
        -->
        <p v-if="!offered" class="security-settings-note">
          パスキーでのログインは、現在ご利用いただけません。
          ご利用をご検討の場合は、サポートへお問い合わせください。
        </p>
        <p v-else-if="!allowedByCompany" class="security-settings-note">
          パスキーの登録は、現在この会社では許可されていません。
          利用するには、会社の管理者による設定が必要です。
        </p>

        <p v-if="!supported" class="security-empty">
          この端末（またはブラウザ）はパスキーに対応していません。
          Windows Hello、Touch ID、スマートフォンの生体認証などが使える
          端末でお試しください。
        </p>

        <template v-else>
          <p v-if="!credentials.length" class="security-empty">
            登録されたパスキーはありません。
          </p>

          <ul v-else class="security-session-list">
            <li
              v-for="item in credentials"
              :key="item.id"
              class="security-session"
            >
              <div class="security-session__body">
                <p class="security-session__title">
                  {{ item.label || '名前のないパスキー' }}
                </p>
                <p class="security-session__detail">
                  登録日時: {{ formatTime(item.createdAt) }}
                </p>
                <p class="security-session__detail">
                  最後に使ったの: {{ formatTime(item.lastUsedAt) }}
                </p>
              </div>
              <CButton
                color="outline-danger"
                size="sm"
                :disabled="busy"
                @click="remove(item)"
              >削除</CButton>
            </li>
          </ul>

          <template v-if="allowedByCompany">
            <div class="passkey-register">
              <CInput
                v-model="label"
                type="text"
                maxlength="60"
                placeholder="この端末の名前（例: 会社のノートPC）"
                :disabled="busy"
              />
              <CButton
                color="primary"
                :disabled="busy"
                @click="register"
              >この端末を登録する</CButton>
            </div>
            <p class="security-settings-note">
              登録すると、この端末の画面ロック（指紋・顔・PIN）で
              ログインできるようになります。
            </p>
          </template>
        </template>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import {
  isPasskeySupported,
  fetchPasskeyRegistrable,
  registerPasskey,
  listPasskeys,
  deletePasskey,
  isReauthenticationRequired,
  isNetworkFailure
} from '../../services/passkey'
import { isLastSignInMethod } from '../../services/externalIdpLogin'
import { describePasskeyRegisterFailure } from '../../common/passkeyRegisterFailure'

/**
 * 自分のパスキーを登録する・消す画面。
 *
 * <h3>登録できることと、ログインに使えることは別</h3>
 *
 * <p>登録はPREIKA全体のスイッチと関係なくできる。ログインに使えるのは
 * スイッチが開いているときだけ。<b>その差を黙っていると、登録したのに
 * 使えない理由が分からない。</b>だから登録済みなのに開いていないときは、
 * その場で伝える。</p>
 *
 * <h3>最後の1つは消させない</h3>
 *
 * <p>パスワードを使わない設定にしている人が、最後のパスキーを消すと
 * 入れなくなる。サーバが断るので、画面はその理由をそのまま出す。</p>
 */
import { PRODUCT_NAME } from '../../common/productName'

export default {
  name: 'PasskeySettings',

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    }
  },

  data () {
    return {
      supported: false,
      /** PREIKA がパスキーのログインを開けているか。 */
      offered: false,
      /** その会社が利用者に許しているか。 */
      allowedByCompany: false,
      credentials: [],
      label: '',
      loading: true,
      busy: false,
      message: '',
      errorMessage: ''
    }
  },

  mounted () {
    this.supported = isPasskeySupported()
    this.reload()
  },

  methods: {
    async reload () {
      this.loading = true
      /*
       * 一覧が読めないことと、閉じていることは別。
       * **一覧の失敗だけをエラーにする。**可否は聞けなければ閉じて
       * いる扱いで、それ自体は異常ではない。
       *
       * ここで /passkey/availability を見てはいけない。あれは
       * ログイン画面向けで会社の設定を含まないので、会社が閉じて
       * いる人にも登録の操作が出る。押すとサーバが断る。
       */
      const 可否 = await fetchPasskeyRegistrable()
      this.offered = 可否.offered
      this.allowedByCompany = 可否.offered && 可否.allowedByCompany

      try {
        this.credentials = await listPasskeys()
      } catch (error) {
        this.credentials = []
        this.errorMessage = this.explain(error,
          '登録済みのパスキーを読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },

    async register () {
      if (this.busy) return
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        await registerPasskey(this.label.trim())
        this.label = ''
        this.message = 'この端末をパスキーとして登録しました。'
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explainRegister(error)
      } finally {
        this.busy = false
      }
    },

    async remove (item) {
      if (this.busy) return
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        await deletePasskey(item.id)
        this.message = 'パスキーを削除しました。'
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(error, 'パスキーを削除できませんでした。')
      } finally {
        this.busy = false
      }
    },

    /**
     * 登録が通らなかったときの見せ方。
     *
     * <p>ブラウザ側で断られる場合（利用者が取り消した、端末が対応して
     * いない、ドメインが合っていない）は、サーバの応答が無い。
     * <b>通信の失敗と混ぜない。</b>混ぜると「接続を確認してください」と
     * 出て、実際には接続の問題ではない。</p>
     */
    explainRegister (error) {
      /*
       * **文言は1か所から取る。**
       *
       * 同じ見分け方が、ログイン直後に出るダイアログにも要る。
       * 写すと片方だけ直る。
       */
      return describePasskeyRegisterFailure(error)
    },

    explain (error, fallback) {
      if (isReauthenticationRequired(error)) {
        return 'もう一度ログインの確認が必要です。'
          + ' 一度ログアウトして再ログインしてから、もう一度お試しください。'
      }
      if (isLastSignInMethod(error)) {
        return this.serverText(error)
          || '有効なログイン方法がこれのみになるため、削除できません。'
          + ' 削除する前に、別のログイン方法を設定してください。'
      }
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      return this.serverText(error) || fallback
    },

    serverText (error) {
      const response = error && error.response
      const data = (response && response.data) || {}
      return typeof data.message === 'string' && data.message
        ? data.message
        : null
    },

    formatTime (value) {
      if (!value) return '未使用'
      const at = new Date(value)
      if (isNaN(at.getTime())) return '不明'
      return at.toLocaleString('ja-JP')
    }
  }
}
</script>
